import React, { useState, useEffect } from 'react';
import { Stack, Box, Divider, Typography, Grid, Tabs, Tab } from "@mui/material";
import LinkButton from "~/components/LinkButton";
import ActionsSlider from "~/components/ActionManagement/Slider";
import descubraSuaCausa from "~/images/descubra-sua-causa.png";
import NGOsMap from "~/components/NGOs/Map";
import CauseGrid from "~/components/Cause/Grid";
import { APP_ROUTES } from "~/single-page/constants/routes";
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';
import SearchField from '~/components/SearchField';
import { useApiCall } from '~/single-page/hooks/useApiCall';
import ActionManagementsClient from "../api/ActionManagementsClient";
import SearchClient from "../api/SearchClient";
import ActionCard from "~/components/ActionManagement/Card";
import NGOCard from "~/components/NGOs/Card";
import { Fade } from "@mui/material";
import SponsorsGrid from "~/components/Sponsors/Grid";

export default function OpportunitiesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentBanner, setCurrentBanner] = useState(0);
  
  const {
    data: allActions = [],
  } = useApiCall({
    apiCall: ActionManagementsClient.actionManagements,
    init: true,
  });

  const {
    data: apiSearchResults = { actions: [], ongs: [] },
    success: searchSuccess,
    pending: searchPending,
    makeApiCall: performSearch
  } = useApiCall({
    apiCall: () => SearchClient.search(searchTerm),
    init: false,
    deps: [searchTerm],
  });

  const banners = [
    { id: 1, image: "/images/main-banner/banner_home_1.jpg", alt: "Banner 1" },
    { id: 2, image: "/images/main-banner/banner_home_2.jpg", alt: "Banner 2" },
    { id: 3, image: "/images/main-banner/banner_home_3.jpg", alt: "Banner 3" }
  ];

  useScrollToSection();

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchTerm) {
        performSearch();
      }
    }, 500);

    const interval = setInterval(() => {
      setCurrentBanner(prev => (prev === banners.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => {
      clearTimeout(delaySearch);
      clearInterval(interval);
    };
  }, [searchTerm, performSearch, banners.length]);

  const actions = apiSearchResults?.actions || [];
  const ongs = apiSearchResults?.ongs || [];

  const isLoading = searchPending;
  
  return (
    <Stack spacing={4} sx={{ maxWidth: '100%' }}>
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          height: { xs: '200px', sm: '300px', md: '400px' },
          overflow: 'hidden',
          marginTop: -2,
          marginX: -2
        }}
      >
        {banners.map((banner, index) => (
          <Fade 
            key={banner.id} 
            in={currentBanner === index} 
            timeout={1000}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: currentBanner === index ? 'block' : 'none'
            }}
          >
            <Box
              component="img"
              src={banner.image}
              alt={banner.alt}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'left center',
              }}
            />
          </Fade>
        ))}
        
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2
          }}
        >
          {banners.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentBanner(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: currentBanner === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
            />
          ))}
        </Box>
      </Box>

      {/* <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, mt: 2, textAlign: 'center' }}>
          Somar para multiplicar
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Bora Impactar, minha gente!</strong> Esta é uma plataforma
          digital totalmente dedicada para gerar conexões. Tudo aqui é feito para
          multiplicar as possibilidades de praticar boas ações com os olhos
          voltados para quem mais precisa. Neste espaço você é nosso parceiro para
          fortalecer e ampliar o trabalho realizado pelos movimentos sociais e
          ONGs.
        </Typography>

        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>No Bora Impactar</strong> abrimos diversas portas e janelas
          conectando quem precisa de ajuda e quem busca por apoio. Duas pontas
          unidas que juntas somam um Recife ainda mais forte. Porque fazer o bem é
          via de mão dupla: ganha quem recebe, mas ganha também quem oferece
          ajuda.
        </Typography>
      </Box> */}

      <Box id="opportunities">
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Oportunidades
        </Typography>
        <SearchField 
          fullWidth
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          placeholder="Pesquisar ações, projetos ou ONGs..."
          helperText="Busque por nome da ação, descrição, projeto ou ONG"
        />
      </Box>

      {searchTerm && (
        <Box>
          <Typography variant="h6" sx={{ mb: 5, textAlign: 'center' }}>
            Resultados da busca
          </Typography>

          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Carregando resultados...</Typography>
            </Box>
          ) : (
            <Box>
              {actions.length > 0 || ongs.length > 0 ? (
                <Grid 
                  container 
                  spacing={2}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr',
                      md: 'repeat(2, 1fr)'
                    },
                    gap: 2,
                    mb: 4
                  }}
                >
                  {actions.map((action) => (
                    <Grid 
                      key={`action-${action.id}`}
                      sx={{ 
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <ActionCard 
                        action={action}
                        sx={{ height: '100%' }}
                      />
                    </Grid>
                  ))}

                  {ongs.map((ong) => (
                    <Grid 
                      key={`ong-${ong.id}`}
                      sx={{ 
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <NGOCard 
                        ong={ong}
                        sx={{ height: '100%' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : !isLoading && searchSuccess && (
                <Box sx={{ 
                  width: '100%', 
                  textAlign: 'center', 
                  py: 4
                }}>
                  <Typography color="text.secondary">
                    Nenhum resultado encontrado com o termo "{searchTerm}"
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ width: "100%" }} />

      <Box id="opportunities" sx={{ scrollMarginTop: '80px' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
          Ações em movimento
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
          Fique por dentro! Confira as ações que estão rolando na cidade. Uma
          delas pode se encaixar no seu perfil e você pode impactar a vida de
          muita gente!
        </Typography>

        <Box sx={{ width: "100%", mb: 4 }}>
          <ActionsSlider actions={allActions} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <LinkButton
            color="secondary"
            sx={{ width: "255px" }}
            size="large"
            to={`${APP_ROUTES.ACTION_MANAGEMENTS}`}
          >
            Ver todas
          </LinkButton>
        </Box>
      </Box>

      <Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
          Conheça nossos patrocinadores
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          Nossos patrocinadores compartilham do mesmo propósito: transformar vidas.
          Juntos, ampliamos o alcance de cada causa e multiplicamos o impacto.
        </Typography>

        <SponsorsGrid />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <LinkButton
            color="secondary"
            sx={{ width: "255px" }}
            size="large"
            to={`${APP_ROUTES.SPONSORS}`}
          >
            Ver todas
          </LinkButton>
        </Box>
      </Box>

      <Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
          Bora Impactar: Conheça nossos parceiros
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          Conheça mais sobre as ONGs e os projetos que fazem parte da nossa
          conexão.
        </Typography>

        <CauseGrid />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <LinkButton
            color="secondary"
            sx={{ width: "255px" }}
            size="large"
            to={`${APP_ROUTES.CAUSES}`}
          >
            Ver todas
          </LinkButton>
        </Box>
      </Box>

      <Stack spacing={0} sx={{ width: '100%' }}>
        <Box
          id="find-your-cause"
          sx={{
            width: '100%',
            height: { xs: "174px", md: "auto" },
            display: "flex",
            justifyContent: "center",
            py: 10,
            pt: 4,
            scrollMarginTop: '80px'
          }}
        >
          <a 
            href="https://descubrasuacausa.net.br/" 
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              width: '100%', 
              maxWidth: '825px',
              display: 'block'
            }}
          >
            <img
              src={descubraSuaCausa}
              alt="descubra sua causa"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </a>
        </Box>

        <Box 
          sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: { 
              xs: 20,
              sm: 20,
              md: 0
            }
          }}
        >
          <NGOsMap />
        </Box>
      </Stack>
    </Stack>
  );
}
